import { Controller } from '@hotwired/stimulus'

/**
 * ClientSideValidationController
 *
 * A Stimulus controller that provides client-side form validation with custom error display.
 * This controller enhances HTML5 form validation by providing better error messaging,
 * accessibility features, and user experience improvements.
 *
 * Features:
 * - Custom error message display with unique error IDs
 * - Accessibility support (aria-invalid, aria-describedby, role="alert")
 * - Automatic scrolling to first error field
 * - Error state management (CSS classes, ARIA attributes)
 * - Error reset functionality
 *
 * Usage:
 * <form data-controller="client-side-validation">
 *   <input data-client-side-validation-target="input" type="email" required data-error-id="email-error" aria-describedby="email-error">
 *   <div class="form-error-text hidden" id="email-error" role="alert"></div>
 *   <button type="submit">Submit</button>
 * </form>
 *
 * CSS Classes:
 * - .form-error-text: Applied to error message elements
 * - .is-invalid: Applied to inputs with validation errors
 * - .hidden: Used to hide error messages when resetting
 */
export default class extends Controller {
  static targets = ['input']

  /**
   * Initializes the controller when connected to the DOM.
   * Sets up event listeners for form submission and error reset events.
   */
  connect() {
    // Reset all errors when form is submitted
    this.element.addEventListener('submit', () => {
      this.inputTargets.forEach(input => this.resetError(input))
    })

    // Reset all errors when resetErrors event is dispatched
    this.element.addEventListener('resetErrors', () => {
      this.inputTargets.forEach(input => this.resetError(input))
    })
  }

  /**
   * Called when an input target is connected to the controller.
   * Sets up validation event listeners and assigns unique error IDs.
   *
   * @param {HTMLElement} element - The input element that was connected
   */
  inputTargetConnected(element) {
    // Generate unique error ID if not already present
    if (element.dataset.errorId === undefined) {
      element.dataset.errorId = `error-${crypto.randomUUID()}`
    }
    // Add invalid event listener for validation
    element.addEventListener('invalid', this.handleInvalid.bind(this))
  }

  /**
   * Handles the 'invalid' event from form inputs.
   * Creates or updates error message elements and applies error styling.
   *
   * @param {Event} event - The invalid event object
   */
  handleInvalid(event) {
    event.preventDefault()

    // Find existing error element or create new one
    const error = document.querySelector(`#${event.target.dataset.errorId}`) || document.createElement('div')

    // Set up error element if it's newly created
    if (error.id !== event.target.dataset.errorId) {
      error.id = event.target.dataset.errorId
      error.role = 'alert' // Accessibility: screen readers will announce this
      error.classList.add('form-error-text')
      error.classList.add('hidden')
      event.target.after(error) // Insert error after the input
    }

    // Display the validation message
    error.textContent = event.target.validationMessage
    error.classList.remove('hidden')

    // Apply error styling and accessibility attributes to all inputs with this error ID
    document.querySelectorAll(`[data-error-id="${event.target.dataset.errorId}"]`).forEach(input => {
      input.setAttribute('aria-invalid', 'true')
      input.classList.add('is-invalid')
    })

    // Delay scroll behavior to make sure it only happens after all inputs have been validated
    clearTimeout(this.errorTimeout)
    this.errorTimeout = setTimeout(this.scrollToFirstError.bind(this), 10)
  }

  scrollToFirstError() {
    // Improve UX: scroll to error and focus the field
    const firstField = this.inputTargets.find(input => input.ariaInvalid === 'true')
    if (!firstField) return

    const firstError = document.querySelector(`[data-error-id="${firstField.dataset.errorId}"]`)
    if (firstError) {
      firstError.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      firstField.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    firstField.focus()
  }

  /**
   * Resets error state for a given input element.
   * Hides error messages and removes error styling.
   *
   * @param {HTMLElement} element - The input element to reset
   */
  resetError(element) {
    // Hide and clear the error message
    const error = document.querySelector(`#${element.dataset.errorId}`)
    if (error) {
      error.classList.add('hidden')
      error.innerHTML = ''
    }

    // Remove error styling and accessibility attributes
    element.setAttribute('aria-invalid', 'false')
    element.classList.remove('is-invalid')
  }
}